import { SearchResults } from "../Shared/scriptts/interfaces";
import { getBoolean, getPrintableCharacters, getRequestVerificationToken, htmlEncode, isEmpty } from "../Shared/scriptts/utilities";
import { prepareAuthenticationAttribution } from "../Shared/scriptts/attribution";
import { Params } from "../Shared/scriptts/search-params";
import { URLCriteriaHelper } from "./search-urlcriteriahelper";
import { buildSearchResultsCards } from "./Templates/search-results-card";
import { getSearchResultsPagination, bindPageButtonEvents } from "./Templates/search-results-pagination";
import { displaySortFilter } from './Templates/search-filter-sort';
import { displayDefaultFilters, initSetDefaultFilterInSession } from './Templates/search-filter-default';
import { displayFilters, initBrandedSearch, addParam } from './search-filters-main';
import { displayJobAlertForm, setSavedSearchParams, updateJobAlertFields } from './Templates/search-filter-job-alert';
import { displayFilterPills } from "./Templates/search-filter-pills";
import { setUserObject, getUserObject } from "../Shared/scriptts/session/sessionmain";
import { logSearch, logSearchFilterInteraction } from "../Shared/scriptts/analytics";
import { addRecentSearch } from "../Shared/scriptts/rootcomponents/autocomplete";
import { displaySearchOptions } from './search-options';

declare var gAliasMapping: any;
declare var gSearchFilters: any;
declare var gBrandedSearch: any;
declare var gCyberWorkRoles: string[];
declare var gIsSearchFiltersEnabled: boolean;
const executeSavedSearchUrl = '/Search/ExecuteStoredSearch';
let isFromEmail = window.location.search.toLowerCase().includes('fromemail=true');
let activeSearchController: AbortController | null = null;
export const executeSearchUrl = '/Search/ExecuteSearch';
export let params: Params;
export const aliasMapping = gAliasMapping;
export let searchFilters = gSearchFilters;
export const brandedSearch = gBrandedSearch;
export const cyberWorkRoles = gCyberWorkRoles;
export let isSavedSearch = false;

window["getQueryParams"] = function () {
    return JSON.stringify(params);
}

document.addEventListener('session-loaded', async () => {
    params = URLCriteriaHelper.ParseURL(params);

    if (params.SavedSearchID !== undefined && params.SavedSearchID !== null) {
        isSavedSearch = true;
        displayResults(executeSavedSearchUrl + `?SavedSearchID=${params.SavedSearchID?.toString()}`, false, true);
    }
    else {
        initBrandedSearch();
        if (gIsSearchFiltersEnabled && !brandedSearch) {
            await initSetDefaultFilterInSession();
        }
        displayResults(executeSearchUrl, false, true);
    }
    document.querySelector<HTMLButtonElement>('#jobs-search')?.addEventListener('click', searchClicked);
    if (!getUserObject()?.IsAuthenticated) {
        document.querySelector<HTMLElement>('#top-nav-login')?.addEventListener('click', signinClick);
    }
});

/**
  * Returns a boolean value that determines whether the current search has a keyword or search filter applied.
  * @param excludeKeywordFromCheck When true, the Keyword parameter is ignored and the function only checks for applied search filters.
  * @returns True if the search has a keyword or filter applied; when `excludeKeyword` is true, returns true only if a non-keyword filter is applied. False otherwise.
 */
export function hasKeywordOrFilter(excludeKeywordFromCheck: boolean = false): boolean {
    for (const key in params) {
        if (excludeKeywordFromCheck && key === "Keyword") { continue; }
        if (key in aliasMapping && aliasMapping[key].SearchFilter) {
            if (params[key] instanceof Array) {
                if (params[key].filter(p => p.toString() !== '').length > 0) return true;
            } else if (!isEmpty(params[key])) {
                return true;
            }
        }
    }
    return false;
}

function updateNoResultsKeyword(keyword: string) {
    const keywordElem = document.querySelector<HTMLElement>('#no-search-results-keyword');
    if (keywordElem) {
        keywordElem.textContent = `for ${keyword.trim()}`;
    }
}

/**
 * Displays the search results based on user-specified keyword and filters
 * @param searchUrl The URL of the search API endpoint to use
 * @param giveFocusToTop If the top of the results should receive focus once results are displayed
 * @param rebuildFilters If the search filters should be rebuilt
 */
export async function displayResults(searchUrl: string, giveFocusToTop = false, rebuildFilters = false) {
    const keywordInput = document.querySelector<HTMLInputElement>('#k');
    const pagingDisplay = document.querySelector<HTMLParagraphElement>('#search-paging-display');
    const filterCountHeader = document.querySelector<HTMLElement>('#filter-job-count');
    const resultsElem = document.querySelector<HTMLElement>('#search-results');
    const pagerElem = document.querySelector<HTMLElement>('#search-results-pagination');
    if (!keywordInput || !pagingDisplay || !resultsElem || !pagerElem) {
        return;
    }

    const careerExplorer = document.querySelector<HTMLDivElement>('#career-explorer');
    const noParams = document.querySelector<HTMLDivElement>('#no-params');
    const noSearchResults = document.querySelector<HTMLDivElement>('#no-search-results');
    const searchesSection = document.querySelector<HTMLDivElement>('#searches-section');
    const selectedPreferencesList = document.querySelector<HTMLDivElement>('#selected-preferences-list');
    const whatNext = document.querySelector<HTMLDivElement>('#what-next');

    setTimeout(() => keywordInput.blur(), 250);
    const loadingElem = document.querySelector<HTMLElement>('#loading');
    let results = <SearchResults>{};

    params.Keyword = (params.Keyword !== undefined && params.Keyword !== null) ? getPrintableCharacters(params.Keyword) : params.Keyword;
    if (!Array.isArray(params.LocationName)) {
        params.LocationName = (isEmpty(params.LocationName)) ? [] : [getPrintableCharacters(params.LocationName)];
    }
    else {
        params.LocationName = params.LocationName.filter(location => location.trim() !== '').map(location => getPrintableCharacters(location));
    }
    pagerElem.innerHTML = '';
    URLCriteriaHelper.reconstructUrl(true);
    activeSearchController?.abort();
    const controller = new AbortController();
    activeSearchController = controller;
    const requestParams = typeof structuredClone !== 'undefined' ? structuredClone(params) : JSON.parse(JSON.stringify(params));
    const loadingTimer = setTimeout(() => {
        if (activeSearchController === controller) {
            loadingElem?.classList.remove('hidden');
        }
    }, 500);

    try {
        const requestVerificationToken = getRequestVerificationToken();
        const response = await fetch(searchUrl, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json; charset=utf-8",
                ...(requestVerificationToken ? { "RequestVerificationToken": requestVerificationToken } : {})
            },
            cache: "no-cache",
            credentials: 'same-origin',
            signal: controller.signal,
            body: JSON.stringify(requestParams)
        });
        if (!response.ok) {
            console.error(response.statusText);
        }
        const data = await response.json();
        if (activeSearchController !== controller) {
            return;
        }
        if (!isSavedSearch && !hasKeywordOrFilter()) {
            pagingDisplay.innerText = '';
            if (filterCountHeader) {
                filterCountHeader.innerText = '';
            }
            resultsElem.innerHTML = '';
            noSearchResults?.classList.add('hidden');
            noParams?.classList.remove('hidden');
            careerExplorer?.classList.remove('hidden');
            selectedPreferencesList?.classList.remove('hidden');
            searchesSection?.classList.remove('hidden');
            whatNext?.classList.remove('hidden');
            displaySearchOptions();
            displayJobAlertForm(false);
            displayFilters(data);
            displaySortFilter(false);
            if (gIsSearchFiltersEnabled && !brandedSearch) {
                displayDefaultFilters(true);
            }
        } else {
            noParams?.classList.add('hidden');
            careerExplorer?.classList.add('hidden');
            selectedPreferencesList?.classList.add('hidden');
            searchesSection?.classList.add('hidden');
            if (gIsSearchFiltersEnabled) {
                whatNext?.classList.add('hidden');
            }
            let effectiveParams = requestParams;
            if (isSavedSearch) {
                results = <SearchResults>data.SearchResult ?? <SearchResults>data;
                if (!isEmpty(data['SavedSearchParams'])) {
                    let savedSearchParams = <Params>JSON.parse(data['SavedSearchParams']);
                    effectiveParams = savedSearchParams;
                    updateJobAlertFields(savedSearchParams);
                    setSavedSearchParams(savedSearchParams);
                    URLCriteriaHelper.reconstructUrl();
                }
            } else {
                results = <SearchResults>data;
                params = URLCriteriaHelper.ParseURL(params);
            }
            if (results.Total > 0) {
                pagingDisplay.innerHTML = getPagingDisplay(results, effectiveParams);
                if (filterCountHeader) {
                    filterCountHeader.innerText = results.Total === 0 ? '0 jobs' : results.Total > 1 ? `${results.Total} jobs` : '1 job';
                }
                buildSearchResultsCards(results);
                pagerElem.innerHTML = getSearchResultsPagination(results.Pager);
                bindPageButtonEvents(pagerElem);
                if (giveFocusToTop === true) {
                    resultsElem.querySelector('a')?.focus();
                }
                noSearchResults?.classList.add('hidden');
            } else {
                resultsElem.innerHTML = '';
                pagingDisplay.innerText = '';
                if (params.Keyword && !isEmpty(params.Keyword)) {
                    updateNoResultsKeyword(params.Keyword);
                }
                noSearchResults?.classList.remove('hidden');
                careerExplorer?.classList.remove('hidden');
                whatNext?.classList.remove('hidden');
            }
            if (rebuildFilters === true) {
                displayFilters(results);
            } else {
                displayFilterPills();
            }
            displayJobAlertForm(!brandedSearch);
            displaySortFilter();
            if (gIsSearchFiltersEnabled && !brandedSearch) {
                displayDefaultFilters();
            }
        }
        updateRecentSearches();
        keywordInput.value = params.Keyword ?? '';
        GALogSearch(results);
    }
    catch (err) {
        if (typeof err === "object" && err !== null && "name" in err && err.name === "AbortError") {
            return;
        }
        return console.error('Error', err);
    }
    finally {
        if (activeSearchController === controller) {
            activeSearchController = null;
            loadingElem?.classList.add('hidden');
        }
        clearTimeout(loadingTimer);
    }
}

export function setParams(value: Params) {
    params = value;
}

export async function signinClick(evt: Event) {
    evt.preventDefault();
    await Promise.race([
        prepareAuthenticationAttribution().catch(error => console.debug('Unable to capture authentication attribution.', error)),
        new Promise<void>(resolve => setTimeout(resolve, 1000))
    ]);
    const searchParameters = window.location.search != "" ? window.location.search.replace(/([?&])(p=\d*)/ig, "$1p=1") + "&auth=True" : "?auth=True";
    location.href = window.location.protocol + "//" + window.location.host + "/Account/Challenge/?returnPath=" + encodeURIComponent("/Search/Results" + searchParameters);
}

function updateRecentSearches() {
    if (params.Keyword && !isEmpty(params.Keyword)) {
        addRecentSearch("keyword", params.Keyword);
    }

    params.LocationName?.forEach(location => addRecentSearch("location", location));
}

function GALogSearch(data: SearchResults) {
    let userObject = getUserObject();
    let isAutocomplete = getBoolean(localStorage.getItem('autocomplete'));
    const isKeywordEmpty = isEmpty(params.Keyword?.toString());

    const getAnalyticsParams = () => <Params>{
        ...params,
        IsAuthenticated: userObject.IsAuthenticated,
        TotalResults: data.Total,
        UniqueSearchID: userObject.UniqueSearch?.Id
    };

    localStorage.removeItem('autocomplete');

    if (!userObject.UniqueSearch) {
        userObject.UniqueSearch = {
            Id: crypto.randomUUID(),
            Keyword: params.Keyword,
            Page: params.Page,
            filters: {
                filterId: crypto.randomUUID()
            },
            total_results: data.Total
        };
        setUserObject(userObject);
        logSearch(getAnalyticsParams(), isAutocomplete);
    }
    else {
        userObject.UniqueSearch.total_results = data.Total;

        if (isKeywordEmpty && userObject.UniqueSearch) {
            userObject.UniqueSearch.Keyword = "";
        }

        if (!isFromEmail && (isKeywordEmpty || (userObject.UniqueSearch && params.Keyword?.toLowerCase() === userObject.UniqueSearch.Keyword?.toLowerCase()))) {
            setUserObject(userObject);
            logSearchFilterInteraction(getAnalyticsParams(), isAutocomplete);
        }
        else {
            isFromEmail = false;
            userObject.UniqueSearch.Id = crypto.randomUUID();
            userObject.UniqueSearch.Keyword = params.Keyword;
            userObject.UniqueSearch.Page = params.Page;
            userObject.UniqueSearch.total_results = data.Total;
            setUserObject(userObject);
            logSearch(getAnalyticsParams(), isAutocomplete);
        }
    }
}

function searchClicked(evt: Event) {
    evt.preventDefault();

    const keywordElem = document.querySelector<HTMLInputElement>('#k');
    const locationElem = document.querySelector<HTMLInputElement>('#l');

    if (!keywordElem || !locationElem) {
        return;
    }

    addParam('Keyword', getPrintableCharacters(keywordElem.value));
    addParam('LocationName', getPrintableCharacters(locationElem.value));

    if (isEmpty(locationElem.value)) {
        params.Radius = undefined;
    }

    displayResults(executeSearchUrl, true, true);
    locationElem.value = '';
}

const getPagingDisplay = (results: SearchResults, params: Params) => {
    const pager = results.Pager;
    const total = results.Total;
    const first = (pager.CurrentPageIndex - 1) * pager.ItemsPerPage + 1;
    const last = pager.CurrentPageIndex * pager.ItemsPerPage;

    const keyword = params.Keyword;
    const locations = params.LocationName;
    
    const parts: string[] = [`${first} &ndash; ${Math.min(last, total)} of ${total}`];

    if (keyword && !isEmpty(keyword)) {
        parts.push(` <span class="hidden md:inline">${htmlEncode(keyword)}</span>`);
    }

    parts.push(total > 1 ? " jobs" : " job");

    if (Array.isArray(locations) && locations.length > 0) {
        parts.push(` <span class="hidden md:inline"> in ${htmlEncode(locations[0])}`);
        if (locations.length > 1) {
            parts.push(` +${locations.length - 1} location(s)`);
        }
        parts.push("</span>");
    }

    return parts.join("");
};
